import { useEffect, useRef, useSyncExternalStore } from 'react'
import { apiFetch, onActiveTenantChange } from './api'

// Live data by push (h 4167-4171): anything on the current work date is
// pushed. The API holds GET /api/stream open (apps/api/src/routes/live-stream.js)
// and says which (topic, date) was written; a screen showing that date fetches
// again through its own route. No data travels on the stream.
//
// One stream per tab, opened while any screen is listening and closed when
// none is. It reopens when the workspace changes, when the server ends it
// (every 50 seconds, or when the token runs out, so access is checked
// again), and after an error with a growing wait. Every ready after the first
// is a resync: something may have been missed in between, so every listening
// screen fetches again.
//
// While the stream is down (or the server is not listening) a screen falls
// back to what it did before push: a slow poll where it had one, and its
// "as at" stamp with a refetch on focus.

export type LiveTopic = 'cycles' | 'shifts' | 'batches' | 'wages'
export interface LiveChange {
  topic: LiveTopic
  date: string
}
export type LiveState = 'off' | 'connecting' | 'live' | 'down'

interface TokenSource {
  getIdToken: () => Promise<string>
}

type Message = { type: 'changes'; changes: LiveChange[] } | { type: 'resync' }
type Listener = (msg: Message) => void

const MAX_BACKOFF_MS = 30_000
// The server writes at least a ": ping" every 25 s (live-stream.js). A stream
// silent for longer than this is dead even though it never ended: a proxy
// that lost its upstream can hold the browser's half open indefinitely, and
// the screen would go on saying it is receiving updates.
let staleMs = 60_000

const listeners = new Set<Listener>()
const stateListeners = new Set<() => void>()
let state: LiveState = 'off'
let tokenSource: TokenSource | null = null
let abort: AbortController | null = null
let generation = 0
let hadReady = false
let backoff = 1000
let retryTimer: ReturnType<typeof setTimeout> | null = null

function setState(next: LiveState) {
  if (next === state) return
  state = next
  stateListeners.forEach((l) => l())
}

function emit(msg: Message) {
  listeners.forEach((l) => l(msg))
}

function stop() {
  generation += 1
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = null
  abort?.abort()
  abort = null
}

function retryLater(gen: number) {
  if (gen !== generation) return
  setState('down')
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = setTimeout(() => open(), backoff)
  backoff = Math.min(backoff * 2, MAX_BACKOFF_MS)
}

// One frame of text/event-stream: its event name and JSON data.
export function parseFrame(block: string): { event: string; data: any } | null {
  let event = 'message'
  const data: string[] = []
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue
    const at = line.indexOf(':')
    const field = at < 0 ? line : line.slice(0, at)
    const value = at < 0 ? '' : line.slice(at + 1).replace(/^ /, '')
    if (field === 'event') event = value
    else if (field === 'data') data.push(value)
  }
  if (!data.length) return null
  try {
    return { event, data: JSON.parse(data.join('\n')) }
  } catch {
    return null
  }
}

function handle(frame: { event: string; data: any }) {
  if (frame.event === 'ready') {
    backoff = 1000
    // A server that is not listening sends nothing: say so, so screens keep
    // their fallback.
    setState(frame.data?.listening ? 'live' : 'down')
    if (hadReady) emit({ type: 'resync' })
    hadReady = true
  } else if (frame.event === 'changes' && Array.isArray(frame.data?.changes)) {
    emit({ type: 'changes', changes: frame.data.changes })
  } else if (frame.event === 'resync') {
    emit({ type: 'resync' })
  }
}

async function open() {
  stop()
  if (!listeners.size || !tokenSource) {
    setState('off')
    return
  }
  const gen = generation
  const ctrl = new AbortController()
  abort = ctrl
  if (state !== 'live') setState('connecting')
  let silent: ReturnType<typeof setTimeout> | null = null
  const watch = () => {
    if (silent) clearTimeout(silent)
    silent = setTimeout(() => {
      if (gen !== generation) return
      stop()
      retryLater(generation)
    }, staleMs)
  }
  try {
    const res = await apiFetch(tokenSource, '/api/stream', { signal: ctrl.signal, headers: { Accept: 'text/event-stream' } })
    if (!res.ok || !res.body) throw new Error(`stream ${res.status}`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    let ready = false
    watch()
    for (;;) {
      const { value, done } = await reader.read()
      if (gen !== generation) return
      if (done) break
      watch()
      buf += decoder.decode(value, { stream: true }).replace(/\r\n?/g, '\n')
      let at
      while ((at = buf.indexOf('\n\n')) >= 0) {
        const f = parseFrame(buf.slice(0, at))
        buf = buf.slice(at + 2)
        if (f) {
          if (f.event === 'ready') ready = true
          handle(f)
        }
      }
    }
    // The server ended it (its lifetime, or a restart): open a new one now,
    // with a fresh token. One that ended before it was ready is a failure,
    // and waits like any other.
    if (gen !== generation) return
    if (ready) open()
    else retryLater(gen)
  } catch {
    if (ctrl.signal.aborted && gen !== generation) return
    retryLater(gen)
  } finally {
    if (silent) clearTimeout(silent)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (listeners.size && state !== 'live') {
      backoff = 1000
      open()
    }
  })
  window.addEventListener('offline', () => {
    if (listeners.size) {
      stop()
      setState('down')
    }
  })
}

onActiveTenantChange(() => {
  if (!listeners.size) return
  hadReady = false
  open()
})

function subscribe(user: TokenSource, fn: Listener) {
  tokenSource = user
  listeners.add(fn)
  if (listeners.size === 1) {
    hadReady = false
    backoff = 1000
    open()
  }
  return () => {
    listeners.delete(fn)
    if (!listeners.size) {
      stop()
      hadReady = false
      setState('off')
    }
  }
}

export function getLiveState() {
  return state
}

export function useLiveState() {
  return useSyncExternalStore(
    (l) => {
      stateListeners.add(l)
      return () => stateListeners.delete(l)
    },
    () => state,
    () => 'off' as LiveState
  )
}

const DEBOUNCE_MS = 400

// A screen listens while `matches` is given: it is called with each changed
// date, and `onChange` runs (once per burst) when one of `topics` changed on a
// date it shows, or on a resync. `matches` null means the view does not show
// today, so it does not listen and keeps its "as at" stamp.
export function useLive(
  user: TokenSource | null | undefined,
  topics: LiveTopic[],
  matches: ((date: string) => boolean) | null,
  onChange: () => void
): LiveState {
  const cb = useRef(onChange)
  cb.current = onChange
  const match = useRef(matches)
  match.current = matches
  // The token is asked for through the latest user, so a screen whose user
  // object is rebuilt on render does not drop and reopen its subscription.
  const who = useRef(user)
  who.current = user
  const enabled = !!user && !!matches
  const topicKey = topics.join(',')
  useEffect(() => {
    if (!enabled) return
    const source: TokenSource = {
      getIdToken: () => (who.current ? who.current.getIdToken() : Promise.reject(new Error('signed out'))),
    }
    const wanted = new Set(topicKey.split(','))
    let timer: ReturnType<typeof setTimeout> | null = null
    const fire = () => {
      if (timer) return
      timer = setTimeout(() => {
        timer = null
        cb.current()
      }, DEBOUNCE_MS)
    }
    const off = subscribe(source, (msg) => {
      if (msg.type === 'resync') return fire()
      const m = match.current
      if (m && msg.changes.some((c) => wanted.has(c.topic) && m(c.date))) fire()
    })
    return () => {
      if (timer) clearTimeout(timer)
      off()
    }
  }, [enabled, topicKey])
  const s = useLiveState()
  return enabled ? s : 'off'
}

// For tests: how long a silent stream is trusted.
export function setLiveStaleMsForTest(ms: number) {
  staleMs = ms
}

// For tests: forget everything between cases.
export function resetLiveForTest() {
  staleMs = 60_000
  stop()
  listeners.clear()
  hadReady = false
  backoff = 1000
  tokenSource = null
  setState('off')
}
